// IG carousel slides — 1080x1350 vertical (4:5, the best feed ratio).
// Renders one slide at a time via /carousel/:set/:n
// The recorder script screenshots each slide → PNG → zips per set.

import { useParams } from "react-router-dom";

// ---------------- Helpers ----------------

const Frame = ({ children, bg = "#0A0A0A", className = "" }) => (
  <div
    data-testid="carousel-slide"
    className={`font-body fixed inset-0 overflow-hidden ${className}`}
    style={{ background: bg, color: "#fff", width: "1080px", height: "1350px" }}
  >
    {/* grain */}
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
      style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }}
    />
    {/* corner brand */}
    <div className="absolute top-12 left-12 text-[12px] uppercase tracking-[0.4em] font-bold opacity-80">
      sweatwithslay
    </div>
    <div className="absolute top-12 right-12 text-[12px] uppercase tracking-[0.4em] font-bold opacity-50" />
    {children}
  </div>
);

const Page = ({ n, total }) => (
  <div className="absolute bottom-12 right-12 text-[12px] uppercase tracking-[0.4em] font-bold opacity-50">
    {String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}
  </div>
);

const SwipeHint = () => (
  <div className="absolute bottom-12 left-12 text-[12px] uppercase tracking-[0.4em] font-bold opacity-70 flex items-center gap-2">
    Swipe →
  </div>
);

// ---------------- Carousel sets ----------------

const PINK = "#FF1B91";
const DARK = "#0A0A0A";

// ---- ABOUT (6 slides) ----
const ABOUT = [
  // 1 — Cover
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-end px-20 pb-32">
        <p className="text-white/85 uppercase tracking-[0.5em] text-base font-bold mb-6">/ Meet your coach</p>
        <h1 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "240px" }}>
          AJ<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "240px" }}>"SlayJ"</span>
        </h1>
        <p className="mt-8 text-white text-3xl font-bold uppercase tracking-tight">
          Founder · Coach · Operator
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 2 — Mantra
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-8">/ Mantra</p>
        <p className="font-serif-italic text-white leading-[1.05]" style={{ fontSize: "84px" }}>
          "Slay is not a<br />
          finish line.<br />
          It's a <span className="text-[#FF1B91]">mindset.</span><br />
          Let's build<br />
          something that<br />
          helps you win."
        </p>
        <p className="mt-10 text-white/55 text-2xl">— AJ</p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 3 — Background
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-8">/ The story</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.88] tracking-tighter" style={{ fontSize: "120px" }}>
          Ex D1<br />
          <span className="text-[#FF1B91]">athlete.</span><br />
          Tech sales.<br />
          Now your<br />
          coach.
        </h2>
        <p className="mt-10 text-white/70 text-2xl leading-snug">
          BBA, Iona College · Division I lacrosse · Enterprise tech sales · DJ as <span className="font-brush text-[#FF1B91] text-3xl">SlayJ</span>.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 4 — Credentials
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-white uppercase tracking-[0.5em] text-base font-bold mb-8">/ Receipts</p>
        <div className="grid grid-cols-2 gap-5">
          {[
            { p: "NASM", s: "CPT" },
            { p: "NFPT", s: "CPT" },
            { p: "NASM", s: "CNC" },
            { p: "CPR", s: "/ AED" },
          ].map((c) => (
            <div key={c.p + c.s} className="border-2 border-white bg-white/10 p-8">
              <div className="flex items-baseline gap-2">
                <span className="font-display font-black text-white text-6xl tracking-tighter leading-none">{c.p}</span>
                <span className="font-brush text-white text-3xl leading-none">{c.s}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 border-2 border-white bg-white/10 p-6 text-center">
          <p className="text-white text-sm uppercase tracking-[0.3em] font-bold">+ Liability insured · ERGO Next</p>
        </div>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 5 — Off the clock
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-8">/ Off the clock</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.88] tracking-tighter" style={{ fontSize: "96px" }}>
          Probably<br />
          <span className="text-[#FF1B91]">too much.</span>
        </h2>
        <ul className="mt-10 space-y-4 text-white/80 text-2xl leading-snug">
          <li>· Board — <span className="text-white font-semibold">Tampa Bay Tech</span></li>
          <li>· Member — <span className="text-white font-semibold">Innovators Council</span></li>
          <li>· HOA board · house projects</li>
          <li>· Pickleball · golf · cycling · row</li>
          <li>· DJ — <span className="font-brush text-[#FF1B91] text-3xl">SlayJ</span></li>
          <li>· Tunnel to Towers climb (annual)</li>
        </ul>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 6 — CTA
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <p className="text-white uppercase tracking-[0.5em] text-base font-bold mb-10">/ Let's go</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "200px" }}>
          Pull<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "220px" }}>up.</span>
        </h2>
        <p className="mt-12 text-white text-4xl font-bold">sweatwithslay.com</p>
        <p className="mt-3 text-white/85 uppercase tracking-[0.4em] text-lg font-bold">@sweatwithslay</p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
];

// ---- PROGRAMS / PRICING (6 slides) ----
const PROGRAMS = [
  // 1 — Cover
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-end px-20 pb-32">
        <p className="text-white/85 uppercase tracking-[0.5em] text-base font-bold mb-6">/ Programs &amp; Pricing</p>
        <h1 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "200px" }}>
          4 ways<br />
          to<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "240px" }}>slay.</span>
        </h1>
        <p className="mt-8 text-white text-3xl font-bold uppercase tracking-tight">
          Pick the level of support you need.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 2 — Essentials
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold">/ Tier 01</p>
          <p className="text-white/40 uppercase tracking-[0.4em] text-xs font-bold">Bronze</p>
        </div>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-3" style={{ fontSize: "140px" }}>
          Essentials
        </h2>
        <p className="font-display font-black text-[#FF1B91] tracking-tighter mb-2" style={{ fontSize: "120px" }}>$89<span className="text-3xl text-white/55"> / mo</span></p>
        <p className="text-white/55 text-xl mb-8 uppercase tracking-tight font-semibold">&lt; $3/day</p>
        <p className="text-white text-2xl font-semibold mb-6 leading-snug">The plan. The app. The freedom.</p>
        <ul className="space-y-3 text-white/80 text-xl leading-snug">
          <li><span className="text-[#FF1B91] mr-2">●</span>Custom Everfit program</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Demo videos + habit tracking</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Program updates every 4 weeks</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Monthly accountability check-in</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>1 week-1 feedback check-in</li>
        </ul>
        <p className="mt-8 text-white/55 text-base uppercase tracking-[0.25em] font-semibold">Perfect for: self-starters</p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 3 — Coaching (most popular)
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-white uppercase tracking-[0.4em] text-sm font-bold">/ Tier 02</p>
          <p className="text-white text-xs uppercase tracking-[0.3em] font-bold bg-black text-white px-3 py-1.5">★ Most Popular</p>
        </div>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-3" style={{ fontSize: "140px" }}>
          Coaching
        </h2>
        <p className="font-display font-black text-white tracking-tighter mb-2" style={{ fontSize: "120px" }}>$179<span className="text-3xl text-white/70"> / mo</span></p>
        <p className="text-white/75 text-xl mb-8 uppercase tracking-tight font-semibold">&lt; $6/day</p>
        <p className="text-white text-2xl font-semibold mb-6 leading-snug">Structure, feedback, support, accountability.</p>
        <ul className="space-y-3 text-white text-xl leading-snug">
          <li><span className="text-black mr-2 font-bold">▸</span>Everything in Essentials, plus:</li>
          <li><span className="text-black mr-2">●</span>Fully customized program</li>
          <li><span className="text-black mr-2">●</span>Weekly accountability check-ins</li>
          <li><span className="text-black mr-2">●</span>Messaging support between check-ins</li>
          <li><span className="text-black mr-2">●</span>Basic nutrition + habit coaching</li>
          <li><span className="text-black mr-2">●</span>Form review &amp; feedback</li>
        </ul>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 4 — VIP
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold">/ Tier 03</p>
          <p className="text-white/40 uppercase tracking-[0.4em] text-xs font-bold">Gold</p>
        </div>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-3" style={{ fontSize: "180px" }}>
          VIP
        </h2>
        <p className="font-display font-black text-[#FF1B91] tracking-tighter mb-2" style={{ fontSize: "120px" }}>$399<span className="text-3xl text-white/55"> / mo</span></p>
        <p className="text-white/55 text-xl mb-8 uppercase tracking-tight font-semibold">&lt; $14/day</p>
        <p className="text-white text-2xl font-semibold mb-6 leading-snug">Your coach, your partner, your progress.</p>
        <ul className="space-y-3 text-white/80 text-xl leading-snug">
          <li><span className="text-[#FF1B91] mr-2 font-bold">▸</span>Everything in Coaching, plus:</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Monthly 1:1 session</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Priority messaging</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Weekly voice / video / call feedback</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Lifestyle: sleep, stress, recovery</li>
          <li><span className="text-[#FF1B91] mr-2">●</span>Event-specific coaching</li>
        </ul>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 5 — Bespoke
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <div className="flex items-baseline justify-between mb-4">
          <p className="text-white uppercase tracking-[0.4em] text-sm font-bold">/ Tier 04</p>
          <p className="text-white/85 uppercase tracking-[0.4em] text-xs font-bold">Bespoke</p>
        </div>
        <h2 className="font-display font-black uppercase text-white leading-[0.88] tracking-tighter mb-3" style={{ fontSize: "120px" }}>
          Fully<br />Customized
        </h2>
        <p className="font-display font-black text-white tracking-tighter mb-2" style={{ fontSize: "100px" }}>Custom</p>
        <p className="text-white/85 text-xl mb-8 uppercase tracking-tight font-semibold">Quoted to fit you</p>
        <p className="text-white text-2xl font-semibold mb-6 leading-snug">Built from scratch. Priced for what you need.</p>
        <ul className="space-y-3 text-white text-xl leading-snug">
          <li><span className="text-black mr-2">●</span>In-person blocks + remote programming</li>
          <li><span className="text-black mr-2">●</span>Sport-specific (lacrosse, pickleball, golf)</li>
          <li><span className="text-black mr-2">●</span>Couples / family / partner training</li>
          <li><span className="text-black mr-2">●</span>Multi-month event camps</li>
          <li><span className="text-black mr-2">●</span>Travel-season programming</li>
        </ul>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 6 — CTA
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-10">/ Pick yours</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "180px" }}>
          Take the<br />
          <span className="text-[#FF1B91]">intake.</span>
        </h2>
        <p className="mt-10 text-white text-3xl font-bold leading-snug">~15 minutes.<br />I'll match you with the right tier.</p>
        <p className="mt-12 text-white text-4xl font-bold">sweatwithslay.com</p>
        <p className="mt-3 text-white/65 uppercase tracking-[0.4em] text-lg font-bold">@sweatwithslay</p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
];

// ---- FAQ (8 slides) ----
const FAQ_SLIDES = [
  // 1 — Cover
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-end px-20 pb-32">
        <p className="text-white/85 uppercase tracking-[0.5em] text-base font-bold mb-6">/ FAQ</p>
        <h1 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "200px" }}>
          Got<br />
          <span className="font-brush font-normal normal-case -rotate-3 inline-block" style={{ fontSize: "240px" }}>questions?</span>
        </h1>
        <p className="mt-8 text-white text-3xl font-bold uppercase tracking-tight">Most-asked, answered.</p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 2 — Do I need a gym
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-6">/ Q1</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-10" style={{ fontSize: "100px" }}>
          Do I need<br />a gym?
        </h2>
        <p className="text-white text-3xl font-semibold leading-snug mb-6">Nope.</p>
        <p className="text-white/80 text-2xl leading-snug">
          Every program is built around your equipment access. Full commercial gym, Peloton + dumbbells, or bodyweight in a hotel room — we make it work. Tell me what you've got in your intake.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 3 — Beginner?
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-white uppercase tracking-[0.5em] text-base font-bold mb-6">/ Q2</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-10" style={{ fontSize: "100px" }}>
          I'm a<br />beginner.<br />Is this<br />for me?
        </h2>
        <p className="text-white text-3xl font-semibold leading-snug mb-6">Absolutely.</p>
        <p className="text-white text-2xl leading-snug">
          Essentials and Coaching are perfect for beginners. I start you with foundational movement patterns, video demos for every lift, and progress you based on how your body adapts.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 4 — I travel
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-6">/ Q3</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-10" style={{ fontSize: "100px" }}>
          I travel<br />constantly.<br />
          <span className="text-[#FF1B91]">Can I keep up?</span>
        </h2>
        <p className="text-white text-2xl leading-snug">
          That's literally why this exists. I travel a lot myself — so travel programming is baked into how I coach. Add the Travel Week add-on for fully custom workouts on the road.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 5 — Cancel
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-white uppercase tracking-[0.5em] text-base font-bold mb-6">/ Q4</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-10" style={{ fontSize: "100px" }}>
          Can I cancel?
        </h2>
        <p className="text-white text-3xl font-semibold leading-snug mb-6">Yes.</p>
        <p className="text-white text-2xl leading-snug">
          You can pause, cancel, or switch tiers between billing cycles — just let me know before your next renewal. I'd rather have clients who stay because they want to.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 6 — Nutrition
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-6">/ Q5</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-10" style={{ fontSize: "100px" }}>
          Do you do<br />
          <span className="text-[#FF1B91]">nutrition?</span>
        </h2>
        <p className="text-white text-3xl font-semibold leading-snug mb-6">Yes — I'm NASM CNC certified.</p>
        <p className="text-white/80 text-2xl leading-snug">
          Coaching includes basic nutrition + habit coaching. VIP includes advanced. Add Custom Macro Build for a fully built-out macro & habit blueprint.
        </p>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 7 — Results timing
  ({ n, total }) => (
    <Frame bg={PINK}>
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-white uppercase tracking-[0.5em] text-base font-bold mb-6">/ Q6</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter mb-10" style={{ fontSize: "100px" }}>
          How long til<br />
          <span className="font-brush font-normal normal-case -rotate-2 inline-block" style={{ fontSize: "120px" }}>results?</span>
        </h2>
        <ul className="text-white text-2xl leading-snug space-y-4">
          <li><span className="font-bold">2-3 wks:</span> Energy, sleep, mood, gym confidence</li>
          <li><span className="font-bold">8-12 wks:</span> Visible body comp changes</li>
          <li><span className="font-bold">Performance metrics</span> usually move first</li>
        </ul>
      </div>
      <SwipeHint />
      <Page n={n} total={total} />
    </Frame>
  ),
  // 8 — CTA
  ({ n, total }) => (
    <Frame bg={DARK}>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-10">/ Got more?</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "180px" }}>
          DM me<br />
          <span className="font-brush font-normal normal-case text-[#FF1B91] -rotate-3 inline-block" style={{ fontSize: "200px" }}>questions.</span>
        </h2>
        <p className="mt-10 text-white text-3xl font-bold">Or take the intake — ~15 min.</p>
        <p className="mt-12 text-white text-4xl font-bold">sweatwithslay.com</p>
        <p className="mt-3 text-white/65 uppercase tracking-[0.4em] text-lg font-bold">@sweatwithslay</p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
];

const LEGACY = [
  // 01 — Cover
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-sm font-bold mb-10">/ Inaugural · September 2026</p>
        <h1 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "160px" }}>
          SWEAT FOR<br />
          <span className="font-brush font-normal normal-case text-[#FF1B91] -rotate-3 inline-block" style={{ fontSize: "180px" }}>Sacrifice.</span>
        </h1>
        <p className="mt-12 text-white text-4xl font-display font-black uppercase tracking-tighter">911 Minutes of Movement.</p>
        <p className="mt-4 font-serif italic text-white/70 text-2xl">Who are you moving for?</p>
      </div>
      <Page n={n} total={total} />
      <SwipeHint />
    </Frame>
  ),
  // 02 — The challenge
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">/ The challenge</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "140px" }}>
          911<br />minutes.
        </h2>
        <p className="mt-10 text-white/85 text-3xl leading-snug max-w-3xl">Complete <span className="text-[#FF1B91] font-semibold">911 minutes</span> of intentional movement during September. Walk, lift, run, bike, stretch, swim, dance, golf, pickleball — anything that gets you moving with purpose.</p>
        <p className="mt-8 font-serif italic text-white text-3xl">Every minute counts.</p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 03 — Who are you moving for
  ({ n, total }) => (
    <Frame bg="#FF1B91">
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "160px" }}>
          Who are<br />you<br />
          <span className="font-brush font-normal normal-case text-[#0A0A0A] -rotate-3 inline-block" style={{ fontSize: "170px" }}>moving for?</span>
        </h2>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 04 — Tim Goss (with photo, full-bleed memorial)
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/uncles/timmy_memorial_zoom.webp"
          alt="Tim Goss — FDNY Marine Co. 1"
          className="w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center 30%", filter: "grayscale(15%) contrast(1.05)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.25) 35%, rgba(10,10,10,0.85) 70%, rgba(10,10,10,1) 100%)" }} />
      </div>
      <div className="absolute inset-x-0 bottom-0 px-20 pb-32">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-4">/ In honor of</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "120px" }}>
          Tim Goss
        </h2>
        <p className="mt-4 font-serif italic text-white/80 text-3xl">"Uncle Timmy"</p>
        <ul className="mt-8 space-y-3 text-white text-2xl">
          <li>FDNY Marine Firefighter · Marine Co. 1</li>
          <li>23 years of service with the FDNY</li>
          <li>Passed June 17, 2026 · 9/11-related illness</li>
        </ul>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 05 — Harry D'Onofrio (with photo, full-bleed memorial)
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/uncles/harry_chief.webp"
          alt="Harry D'Onofrio — West Sayville Fire Chief"
          className="w-full h-full"
          style={{ objectFit: "cover", objectPosition: "center 25%", filter: "grayscale(15%) contrast(1.05)" }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.15) 30%, rgba(10,10,10,0.85) 70%, rgba(10,10,10,1) 100%)" }} />
      </div>
      <div className="absolute inset-x-0 bottom-0 px-20 pb-32">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-4">/ In honor of</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "108px" }}>
          Harry<br />D&apos;Onofrio
        </h2>
        <p className="mt-4 font-serif italic text-white/80 text-3xl">"Uncle Harry"</p>
        <ul className="mt-8 space-y-3 text-white text-2xl">
          <li>NYPD ESU · West Sayville Fire Chief</li>
          <li>42 years in fire · 20+ years NYPD detective</li>
          <li>Passed May 15, 2021 · 9/11-related illness</li>
        </ul>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 06 — Why I'm doing this (AJ's why)
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">/ Why I'm doing this</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.88] tracking-tighter" style={{ fontSize: "100px" }}>
          Two uncles.<br />
          <span className="text-[#FF1B91]">One mission.</span>
        </h2>
        <p className="mt-10 text-white text-2xl leading-snug">
          I lost <span className="font-semibold">Uncle Harry in 2021</span>. <span className="font-semibold">Uncle Timmy this June</span>. Both to 9/11-related illness.
        </p>
        <p className="mt-6 text-white/80 text-2xl leading-snug">
          When Timmy was sick, <span className="text-white font-semibold">FDNY Fire Family Transport</span> drove him to Sloan Kettering. My cousin Danielle ran NYC for <span className="text-white font-semibold">Tunnel to Towers</span> in Harry&apos;s name.
        </p>
        <p className="mt-8 font-serif italic text-white text-3xl leading-tight">
          This challenge gives back to the causes that carried us — and asks you to move for someone you carry too.
        </p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 07 — How to join
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">/ How to join</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "120px" }}>
          Two ways<br />in.
        </h2>
        <ul className="mt-10 space-y-6 text-white/85 text-3xl">
          <li><span className="text-[#FF1B91] font-bold">01 ·</span> Purchase Sweat for Sacrifice merch <span className="text-white/55 text-xl">(coming soon)</span></li>
          <li><span className="text-[#FF1B91] font-bold">02 ·</span> $20 minimum donation entry</li>
        </ul>
        <p className="mt-10 text-white/65 text-2xl">Custom amounts welcome. Sign-up closes <span className="text-white font-semibold">Aug 31, 2026</span>.</p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 08 — Where proceeds go
  ({ n, total }) => (
    <Frame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.4em] text-sm font-bold mb-8">/ Where proceeds go</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.9] tracking-tighter" style={{ fontSize: "110px" }}>
          For the<br />causes that<br /><span className="text-[#FF1B91]">carried us.</span>
        </h2>
        <ul className="mt-10 space-y-5 text-white text-3xl">
          <li><span className="font-bold">Tunnel to Towers</span> <span className="text-white/55 text-xl block">Smart homes for catastrophically injured veterans &amp; first responders.</span></li>
          <li className="pt-2"><span className="font-bold">FDNY Fire Family Transport</span> <span className="text-white/55 text-xl block">Drives FDNY members &amp; families to medical treatment.</span></li>
        </ul>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
  // 09 — Final CTA
  ({ n, total }) => (
    <Frame bg="#FF1B91">
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "150px" }}>
          Move in<br />their <span className="font-brush font-normal normal-case text-[#0A0A0A] -rotate-3 inline-block" style={{ fontSize: "170px" }}>legacy.</span>
        </h2>
        <p className="mt-12 text-white text-3xl font-bold uppercase tracking-[0.3em]">Sign up at</p>
        <p className="mt-3 text-white font-display font-black uppercase tracking-tighter leading-none" style={{ fontSize: "60px" }}>sweatwithslay.com</p>
        <p className="mt-2 font-brush font-normal normal-case text-[#0A0A0A]" style={{ fontSize: "84px", lineHeight: 1 }}>/legacy</p>
        <p className="mt-12 text-white/85 uppercase tracking-[0.4em] text-base font-bold">@sweatwithslay · #SweatForSacrifice</p>
      </div>
      <Page n={n} total={total} />
    </Frame>
  ),
];

// ---- LEGACY STORIES (1080x1920, 9:16) ----
const StoryFrame = ({ children, bg = "#0A0A0A" }) => (
  <div
    data-testid="carousel-slide"
    className="font-body fixed inset-0 overflow-hidden"
    style={{ background: bg, color: "#fff", width: "1080px", height: "1920px" }}
  >
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
      style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }}
    />
    <div className="absolute top-16 left-16 text-[14px] uppercase tracking-[0.4em] font-bold opacity-80">sweatwithslay</div>
    {children}
  </div>
);

const LEGACY_STORY = [
  // 1 — Cover
  ({ n, total }) => (
    <StoryFrame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-12">/ Inaugural · September 2026</p>
        <h1 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "180px" }}>
          SWEAT FOR
        </h1>
        <p className="font-brush font-normal normal-case text-[#FF1B91] -rotate-3 inline-block mt-6" style={{ fontSize: "220px", lineHeight: 1 }}>
          Sacrifice.
        </p>
        <p className="mt-16 text-white text-5xl font-display font-black uppercase tracking-tighter">911 Minutes of Movement.</p>
        <p className="mt-6 font-serif italic text-white/70 text-3xl">Who are you moving for?</p>
      </div>
      <div className="absolute bottom-20 left-0 right-0 text-center text-white/65 uppercase tracking-[0.4em] text-base font-bold">
        Swipe up · sweatwithslay.com/legacy
      </div>
    </StoryFrame>
  ),
  // 2 — Hook
  ({ n, total }) => (
    <StoryFrame bg="#FF1B91">
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "180px" }}>
          Who are<br />you
        </h2>
        <p className="font-brush font-normal normal-case text-[#0A0A0A] -rotate-3 mt-10 leading-none" style={{ fontSize: "200px" }}>
          moving for?
        </p>
      </div>
      <div className="absolute bottom-20 left-0 right-0 text-center text-white uppercase tracking-[0.4em] text-base font-bold">
        911 minutes · September 2026
      </div>
    </StoryFrame>
  ),
  // 3 — Why (AJ's mission)
  ({ n, total }) => (
    <StoryFrame bg="#0A0A0A">
      <div className="absolute inset-0 flex flex-col justify-center px-20">
        <p className="text-[#FF1B91] uppercase tracking-[0.5em] text-base font-bold mb-10">/ Why I&apos;m doing this</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.88] tracking-tighter" style={{ fontSize: "130px" }}>
          Two<br />uncles.<br />
          <span className="text-[#FF1B91]">One mission.</span>
        </h2>
        <p className="mt-16 text-white text-3xl leading-snug">
          I lost <span className="font-semibold">Uncle Harry in 2021</span>. <span className="font-semibold">Uncle Timmy this June</span>. Both to 9/11-related illness.
        </p>
        <p className="mt-8 font-serif italic text-white text-3xl leading-tight">
          This challenge gives back to the causes that carried us — and asks you to move for someone you carry too.
        </p>
      </div>
      <div className="absolute bottom-20 left-0 right-0 text-center text-white/65 uppercase tracking-[0.4em] text-base font-bold">
        sweatwithslay.com/legacy
      </div>
    </StoryFrame>
  ),
  // 4 — Final CTA
  ({ n, total }) => (
    <StoryFrame bg="#FF1B91">
      <div className="absolute inset-0 flex flex-col items-center justify-center px-20 text-center">
        <p className="text-white uppercase tracking-[0.5em] text-base font-bold mb-12">/ Join us</p>
        <h2 className="font-display font-black uppercase text-white leading-[0.85] tracking-tighter" style={{ fontSize: "160px" }}>
          Move in<br />their
        </h2>
        <p className="font-brush font-normal normal-case text-[#0A0A0A] -rotate-3 mt-8" style={{ fontSize: "220px", lineHeight: 1 }}>
          legacy.
        </p>
        <p className="mt-16 text-white text-3xl font-bold uppercase tracking-[0.3em]">Sign up at</p>
        <p className="mt-4 text-white font-display font-black uppercase tracking-tighter leading-none" style={{ fontSize: "72px" }}>sweatwithslay.com</p>
        <p className="mt-2 font-brush font-normal normal-case text-[#0A0A0A]" style={{ fontSize: "100px", lineHeight: 1 }}>/legacy</p>
      </div>
      <div className="absolute bottom-20 left-0 right-0 text-center text-white uppercase tracking-[0.4em] text-base font-bold">
        @sweatwithslay · #SweatForSacrifice
      </div>
    </StoryFrame>
  ),
];

const SETS = {
  about: ABOUT,
  programs: PROGRAMS,
  faq: FAQ_SLIDES,
  legacy: LEGACY,
  "legacy-story": LEGACY_STORY,
};

export default function CarouselSlide() {
  const { set, n } = useParams();
  const slides = SETS[set];
  const idx = (parseInt(n, 10) || 1) - 1;
  if (!slides || idx < 0 || idx >= slides.length) {
    return (
      <div data-testid="carousel-slide" className="font-body fixed inset-0 flex items-center justify-center bg-black text-white">
        <div className="text-center">
          <p className="font-display font-black uppercase text-3xl">Slide not found</p>
          <p className="mt-2 text-white/60">Try /carousel/about/1, /carousel/programs/1, /carousel/faq/1</p>
        </div>
      </div>
    );
  }
  const Slide = slides[idx];
  return <Slide n={idx + 1} total={slides.length} />;
}

export { SETS };
